import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AutoLocaleBanner } from "@/components/AutoLocaleBanner";
import { Toaster } from "@/components/ui/sonner";
import { captureRefFromUrl } from "@/lib/referral";
import { Landing } from "@/pages/Landing";
import { Configurator } from "@/pages/Configurator";
import { Results } from "@/pages/Results";
import { Report } from "@/pages/Report";
import { SharedReport } from "@/pages/SharedReport";
import { Projects } from "@/pages/Projects";
import { Compare } from "@/pages/Compare";
import { Login } from "@/pages/Login";
import { ResetPassword } from "@/pages/ResetPassword";
import { AuthCallback } from "@/pages/AuthCallback";
import { PaymentSuccess } from "@/pages/PaymentSuccess";
import { PaymentCancel } from "@/pages/PaymentCancel";
import { Admin } from "@/pages/Admin";
import AdminFeedback from "@/pages/AdminFeedback";
import AdminPromos from "@/pages/AdminPromos";
import Pricing from "@/pages/Pricing";
import { Legal } from "@/pages/Legal";
import { PreviewExperience } from "@/pages/PreviewExperience";
import { UnlockedReports } from "@/pages/UnlockedReports";
import { Profile } from "@/pages/Profile";
import { ProfileSettings } from "@/pages/ProfileSettings";
import { initSentry } from "@/lib/sentry";

// iter45al — Sentry init. Gated on REACT_APP_SENTRY_DSN — a no-op in dev
// and preview because the env var is unset there. PII scrubber is inside
// `src/lib/sentry.js` and verified by `src/lib/__tests__/sentry.test.js`.
// Sentry ships to production ONLY when the DSN env var is set on the
// production build.
initSentry();

const Router = () => {
  const location = useLocation();
  // Capture ?ref= on every route change (idempotent — stashes to localStorage only).
  captureRefFromUrl();
  // CRITICAL: detect session_id in fragment synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/configurator" element={<Configurator />} />
      <Route path="/results" element={<Results />} />
      <Route path="/report/:id" element={<Report />} />
      <Route path="/shared/:token" element={<SharedReport />} />
      <Route path="/projects" element={<Projects />} />
      <Route path="/compare" element={<Compare />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/feedback" element={<AdminFeedback />} />
      <Route path="/admin/promos" element={<AdminPromos />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/sectors" element={<Pricing />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/refund-policy" element={<Legal />} />
      <Route path="/legal/:doc" element={<Legal />} />
      <Route path="/preview/experience" element={<PreviewExperience />} />
      <Route path="/reports" element={<UnlockedReports />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/profile/settings" element={<ProfileSettings />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <Router />
            <AutoLocaleBanner />
            <Toaster theme="light" position="bottom-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
